import { useEffect, useReducer, useCallback, useRef, useState } from 'react';
import _ from 'underscore';
import {
  SuggestionPoller,
  SuggestionStatus,
  mapSuggestionResponseToState,
} from '@bingads-webui-campaign/media-repository';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { MessageBarType } from '@bingads-webui-react/fabric-control';

// for every request to poll, we use debounce of 1000ms.
const DebouncedPollWaitMs = 1000;

const websiteSuggestionReducer = (state, action) => {
  const {
    type,
    response,
    isSubdomainScraping,
    error,
    selectedIndex,
  } = action;

  if (type === SuggestionStatus.SelectSuggestionError) {
    return {
      ...state,
      carousel: {
        ...state.carousel,
        message: {
          type: MessageBarType.error,
          message: error.errorMessage,
        },
      },
    };
  }

  if (type === SuggestionStatus.SelectSuggestionComplete) {
    return {
      ...state,
      carousel: {
        ...state.carousel,
        images: _.reject(state.carousel.images, (val, ind) => ind === selectedIndex),
        message: undefined,
      },
    };
  }

  const updatedSuggestions = mapSuggestionResponseToState(type, response, isSubdomainScraping);

  // in subdomain scraping case, carousel state isn't updated; keep existing suggestion carousel state
  if (!updatedSuggestions.carousel) {
    updatedSuggestions.carousel = state.carousel;
  }

  return updatedSuggestions;
};

export const useWebsiteSuggestion = ({
  oDataConfig,
  adContext,
  instrumentation,
  logActivity,
  permissions,
  fullUrl,
  hasErrorInUrl,
  headlines,
  descriptions,
  adCreatorScenario,
  customizedGetMinimumSizeForImage = null,
}) => {
  const [
    suggestionState,
    dispatchSuggestionState,
  ] = useReducer(
    websiteSuggestionReducer,
    fullUrl ? {} : mapSuggestionResponseToState(SuggestionStatus.EmptyURL)
  );

  const onPollingSuggestionCompleted = (response, isSubdomainScraping) => {
    dispatchSuggestionState({
      type: response.error ? SuggestionStatus.ServerError : SuggestionStatus.FetchSucceed,
      response,
      isSubdomainScraping,
    });
  };

  // could not replace it with useMemo or useRef
  // The carousel will be refreshed twice. So still keep this way
  const [poller] = useState(() => {
    const { websiteSuggestionActivity: logger } =
        getAdCreatorInstrumentation(instrumentation, adCreatorScenario, permissions);

    return new SuggestionPoller({
      cid: adContext.advertiserCustomerId,
      aid: adContext.accountId,
      cmpId: adContext.campaignId,
      oDataConfig,
      jobCompleteCallback: onPollingSuggestionCompleted,
      logger,
    });
  });

  // this is to support API calls directly from suggestionPoller
  const pollerWrapperRef = useRef({});

  pollerWrapperRef.current.getLandingPageSuggestions =
    useCallback((opts) => {
      logActivity(`Under Image extensions: getLandingPageSuggestions: opts: ${JSON.stringify(opts)}`);

      return poller.getLandingPageSuggestions({
        ...opts,
        headlines,
        descriptions,
      });
    }, [descriptions, headlines, logActivity, poller]);

  pollerWrapperRef.current.getMoreLandingPageSuggestions =
    useCallback((opts) => {
      logActivity(`Under Image extensions: getMoreLandingPageSuggestions: opts: ${JSON.stringify(opts)}`);

      return poller.getMoreLandingPageSuggestions({
        ...opts,
        headlines,
        descriptions,
      });
    }, [descriptions, headlines, logActivity, poller]);

  const fetchSuggestionByUrl =
    useCallback(_.debounce(
      (url, headlinesArg, descriptionsArg, enableOfflinePollingDelay = false) => {
        dispatchSuggestionState({ type: SuggestionStatus.FetchInProgress });

        const [width, height] = customizedGetMinimumSizeForImage ? customizedGetMinimumSizeForImage() : constants.getMinimumSizeForImage();

        logActivity(`Under Image extensions: get landing page suggestions: url: ${url}; headlinesArgs: ${headlinesArg}; descriptionsArg: ${descriptionsArg}`);

        poller.getLandingPageSuggestions({
          landingPageUrl: url,
          headlines: headlinesArg,
          descriptions: descriptionsArg,
          enableOfflinePollingDelay,
          minimumResolution: { width, height },
        });
      },
      DebouncedPollWaitMs
    ), [poller]);

  useEffect(() => {
    poller.cancel();

    if (_.isEmpty(fullUrl)) {
      dispatchSuggestionState({ type: SuggestionStatus.EmptyURL });
    } else if (hasErrorInUrl) {
      dispatchSuggestionState({ type: SuggestionStatus.InvalidURL });
    } else {
      fetchSuggestionByUrl(
        fullUrl,
        headlines,
        descriptions
      );
    }

    return () => {
      poller.cancel();
    };
  }, [descriptions, fetchSuggestionByUrl, fullUrl, hasErrorInUrl, headlines, poller]);

  return {
    suggestionState,
    suggestionPoller: pollerWrapperRef.current,
    dispatchSuggestionState,
  };
};
